import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { animate, motion, useMotionValue, useReducedMotion, useSpring, useTransform, useVelocity } from "motion/react";
import { ArrowRight, MagnifyingGlass } from "@phosphor-icons/react";
import { EASE_OUT, MaskWords, PageFrame, Seo } from "../../kit.jsx";
import { GRID_MARK as M } from "../../components/gridMarkPaths.js";
import { siteRoutes } from "../../data/routes.js";
import { Rise } from "./shared.jsx";
import "./join.css";

/* ---------- The lost person ---------- */

const [VX, VY, VW] = M.viewBox.split(" ").map(Number);
const FLOOR = 770;
const [HX, HY] = M.headCenter;
const [SX, SY, EX, EY] = M.smileBox;
const MID = (SX + EX) / 2;
const SMILE = `M${SX + 3} ${SY + 2} Q ${MID} ${EY + 14} ${EX - 3} ${SY + 2}`;
const FROWN = `M${SX + 3} ${EY + 4} Q ${MID} ${SY - 10} ${EX - 3} ${EY + 4}`;
const PAD = 340;
const BOX = { x: VX - PAD, y: VY - 170, w: VW + PAD * 2, h: FLOOR + 26 - (VY - 170) };
const MIN_DX = BOX.x + 80 - HX;
const MAX_DX = BOX.x + BOX.w - 80 - HX;
const START_DX = VX + VW + 190 - HX;
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

function Part({ name, ...props }) {
  return <path transform={M[name].transform} d={M[name].d} {...props} />;
}

function LostMark({ goingHome, onHome, onPersonClick }) {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const target = useMotionValue(reduceMotion ? START_DX : 0);
  const dx = useSpring(target, { stiffness: 60, damping: 14, mass: 0.9 });
  const velocity = useVelocity(dx);
  const hop = useTransform(velocity, (v) => -Math.min(46, Math.abs(v) / 28));
  const lean = useTransform(velocity, (v) => clamp(v / 90, -10, 10));
  const lift = useMotionValue(0);
  const y = useTransform([hop, lift], ([a, b]) => a + b);
  const lookX = useMotionValue(0);
  const lookY = useMotionValue(0);
  const headX = useSpring(lookX, { stiffness: 140, damping: 16 });
  const headY = useSpring(lookY, { stiffness: 140, damping: 16 });
  const [out, setOut] = useState(reduceMotion);
  const [hovered, setHovered] = useState(false);
  const lastMove = useRef(0);
  const pointerX = useRef(null);

  // Opening: the person hops out of the R and lands beside the D.
  useEffect(() => {
    if (reduceMotion) return undefined;
    const timer = window.setTimeout(() => {
      setOut(true);
      target.set(START_DX);
      animate(lift, [0, -150, 0], { duration: 1.1, ease: [0.33, 1, 0.68, 1], times: [0, 0.4, 1] });
    }, 900);
    return () => window.clearTimeout(timer);
  }, [lift, reduceMotion, target]);

  // Follow the pointer along the floor. After a few quiet seconds, wander a little on its own.
  useEffect(() => {
    if (reduceMotion || goingHome) return undefined;
    const toSvgX = (clientX) => {
      const box = ref.current?.getBoundingClientRect();
      if (!box) return null;
      return BOX.x + ((clientX - box.left) / box.width) * BOX.w;
    };
    const onMove = (event) => {
      if (!out) return;
      const sx = toSvgX(event.clientX);
      if (sx == null) return;
      pointerX.current = sx;
      lastMove.current = Date.now();
      const box = ref.current.getBoundingClientRect();
      const sy = BOX.y + ((event.clientY - box.top) / box.height) * BOX.h;
      const headAt = HX + dx.get();
      lookX.set(clamp((sx - headAt) / 30, -9, 9));
      lookY.set(clamp((sy - HY) / 40, -6, 7));
      const wanted = clamp(sx - HX + (sx > headAt ? -150 : 150), MIN_DX, MAX_DX);
      target.set(wanted);
    };
    const wander = window.setInterval(() => {
      if (!out || Date.now() - lastMove.current < 3500) return;
      target.set(clamp(target.get() + (Math.random() - 0.5) * 520, MIN_DX, MAX_DX));
    }, 4200);
    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerdown", onMove, { passive: true });
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerdown", onMove);
      window.clearInterval(wander);
    };
  }, [dx, goingHome, lookX, lookY, out, reduceMotion, target]);

  // Going home: hop back into the R, close the gap, smile, then leave the page.
  useEffect(() => {
    if (!goingHome) return undefined;
    if (reduceMotion) {
      onHome();
      return undefined;
    }
    lookX.set(0);
    lookY.set(0);
    target.set(0);
    animate(lift, [0, -170, 0], { duration: 1.2, ease: [0.33, 1, 0.68, 1], times: [0, 0.45, 1] });
    const back = window.setTimeout(() => setOut(false), 1150);
    const leave = window.setTimeout(onHome, 2300);
    return () => {
      window.clearTimeout(back);
      window.clearTimeout(leave);
    };
  }, [goingHome, lift, lookX, lookY, onHome, reduceMotion, target]);

  const happy = goingHome || hovered;

  return (
    <svg
      ref={ref}
      className="jn-nf2-mark"
      viewBox={`${BOX.x} ${BOX.y} ${BOX.w} ${BOX.h}`}
      role="img"
      aria-label="The GRID logo. The person who lives inside the R has wandered off."
    >
      <defs>
        <mask id="jn-nf2-person" maskUnits="userSpaceOnUse" x={BOX.x} y={BOX.y} width={BOX.w} height={BOX.h}>
          <rect x={BOX.x} y={BOX.y} width={BOX.w} height={BOX.h} fill="#fff" />
          <motion.g
            fill="#000"
            initial={false}
            animate={{ opacity: out ? 0 : 1 }}
            transition={{ duration: 0.35, ease: EASE_OUT }}
          >
            <Part name="head" />
            <Part name="body" />
          </motion.g>
        </mask>
      </defs>

      <g fill="#000">
        <Part name="G" />
        <g mask="url(#jn-nf2-person)"><Part name="R" /></g>
        <Part name="I" />
        <Part name="D" />
      </g>

      <motion.g
        className="jn-nf2-ghost"
        initial={false}
        animate={{ opacity: out ? 1 : 0 }}
        transition={{ duration: 0.5, delay: out ? 0.3 : 0 }}
        aria-hidden="true"
      >
        <Part name="head" />
        <Part name="body" />
      </motion.g>

      <motion.path
        d={SMILE}
        fill="none"
        stroke="#000"
        strokeWidth="5"
        strokeLinecap="round"
        initial={false}
        animate={{ opacity: out ? 0 : 1 }}
        transition={{ duration: 0.3, delay: out ? 0 : 0.2 }}
      />

      <motion.g
        className="jn-nf2-person"
        style={{ x: dx, y, rotate: lean, transformBox: "fill-box", transformOrigin: "50% 100%" }}
        initial={false}
        animate={{ opacity: out ? 1 : 0 }}
        transition={{ duration: 0.25 }}
        onClick={onPersonClick}
        onPointerEnter={() => setHovered(true)}
        onPointerLeave={() => setHovered(false)}
      >
        <Part name="body" className="jn-nf2-person__part" />
        <motion.g style={{ x: headX, y: headY }}>
          <Part name="head" className="jn-nf2-person__part" />
          <motion.path
            d={FROWN}
            animate={{ d: happy ? SMILE : FROWN }}
            transition={{ duration: 0.4, ease: EASE_OUT }}
            fill="none"
            stroke="#000"
            strokeWidth="5"
            strokeLinecap="round"
          />
        </motion.g>
      </motion.g>
    </svg>
  );
}

/* ---------- Finding the page ---------- */

function editDistance(a, b) {
  const row = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    let previous = row[0];
    row[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const current = row[j];
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, previous + (a[i - 1] === b[j - 1] ? 0 : 1));
      previous = current;
    }
  }
  return row[b.length];
}

const PAGES = siteRoutes.filter((item) => item.path !== "/");

function closestPages(pathname) {
  const wanted = pathname.toLowerCase().replace(/\/+$/, "");
  const words = wanted.split(/[/\-_]+/).filter(Boolean);
  return PAGES.map((item) => {
    const path = item.path.toLowerCase();
    const distance = editDistance(wanted, path) / Math.max(wanted.length, path.length);
    const tail = editDistance(words.at(-1) ?? "", path.split("/").at(-1) ?? "") / Math.max(1, (words.at(-1) ?? "").length);
    const shared = words.filter((word) => word.length > 2 && `${path} ${item.title.toLowerCase()}`.includes(word)).length;
    return { item, score: Math.min(distance, tail * 0.9) - shared * 0.2 };
  })
    .sort((a, b) => a.score - b.score)
    .filter((entry) => entry.score < 0.55)
    .slice(0, 3)
    .map((entry) => entry.item);
}

const POPULAR = ["/programmes/aaroh", "/impact", "/get-involved"].map((path) => siteRoutes.find((item) => item.path === path)).filter(Boolean);

function PageRow({ item, index }) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.li
      initial={reduceMotion ? false : { opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.7, delay: index * 0.06, ease: EASE_OUT }}
    >
      <Link to={item.path} className="jn-nf2-row">
        <span className="jn-nf2-row__thumb"><img src={item.heroMedia} alt="" loading="lazy" decoding="async" /></span>
        <span className="jn-nf2-row__text">
          <strong>{item.title}</strong>
          <small>{item.description}</small>
        </span>
        <code>{item.path}</code>
        <span className="jn-nf2-row__arrow" aria-hidden="true"><ArrowRight weight="bold" /></span>
      </Link>
    </motion.li>
  );
}

function SiteSearch({ inputRef }) {
  const [query, setQuery] = useState("");
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return siteRoutes
      .filter((item) => `${item.title} ${item.description} ${item.path}`.toLowerCase().includes(q))
      .slice(0, 8);
  }, [query]);
  return (
    <section className="jn-nf2-search" aria-labelledby="jn-nf2-search-title">
      <h2 id="jn-nf2-search-title">Search all {siteRoutes.length} pages</h2>
      <label className="jn-nf2-input">
        <MagnifyingGlass weight="bold" aria-hidden="true" />
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Try AAROH, guides or internships"
          aria-label="Search pages"
        />
      </label>
      {query.trim() ? (
        results.length ? (
          <ul className="jn-nf2-list" aria-live="polite">
            {results.map((item, index) => <PageRow key={item.path} item={item} index={index} />)}
          </ul>
        ) : (
          <p className="jn-nf2-none" aria-live="polite">No page matches "{query}". Try a shorter word, or write to founder@projectgrid.org.</p>
        )
      ) : null}
    </section>
  );
}

export default function NotFoundPage() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [goingHome, setGoingHome] = useState(false);
  const matches = useMemo(() => closestPages(pathname), [pathname]);
  const suggestions = matches.length ? matches : POPULAR;

  return (
    <PageFrame className="rd-page jn-page jn-nf2">
      <Seo title="Page not found" path="/404" description="This Project GRID page could not be found." />
      <main className="jn-nf2-main">
        <section className="jn-nf2-hero" aria-labelledby="jn-nf2-title">
          <header className="jn-nf2-head">
            <Rise as="p" className="jn-mono" delay={0.05}>Error 404</Rise>
            <MaskWords as="h1" id="jn-nf2-title" className="jn-nf2-title" text="Someone wandered off. So did this page." onMount delay={0.1} />
          </header>
          <LostMark goingHome={goingHome} onHome={() => navigate("/")} onPersonClick={() => setGoingHome(true)} />
          <div className="jn-nf2-foot">
            <Rise as="p" className="jn-nf2-lede" delay={0.5}>
              We could not find <code>{pathname}</code>. The link may be old or mistyped. Help them back into the R, or pick a page below.
            </Rise>
            <Rise className="gh-actions" delay={0.6}>
              <button type="button" className="gh-btn gh-btn--fill" onClick={() => setGoingHome(true)} disabled={goingHome}>
                <span className="roll" data-label="Take them home"><span>Take them home</span></span>
              </button>
              <button type="button" className="gh-btn gh-btn--ghost" onClick={() => inputRef.current?.focus()}>
                <span className="roll" data-label="Search the site"><span>Search the site</span></span>
              </button>
            </Rise>
          </div>
        </section>

        <section className="jn-nf2-guess" aria-labelledby="jn-nf2-guess-title">
          <h2 id="jn-nf2-guess-title">{matches.length ? "Did you mean one of these?" : "Most people are looking for these"}</h2>
          <ul className="jn-nf2-list">
            {suggestions.map((item, index) => <PageRow key={item.path} item={item} index={index} />)}
          </ul>
        </section>

        <SiteSearch inputRef={inputRef} />
      </main>
    </PageFrame>
  );
}
